import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AuthService } from '../../../authentification/login/service';
import { EChangeTarget } from '../../../shared/enums/EChangeTarget';
import { formatDate } from '../../../shared/functions/formatDate';
import { formatNumber } from '../../../shared/functions/formatNumber';
import { handleError } from '../../../shared/functions/handleError';
import { IChangeTarget } from '../../../shared/interfaces/IChangeTarget';
import { ITruck, ITruckService } from '../../../shared/interfaces/ITruck';

interface IProps {
  truckId: number;
}
const TruckPreview = ({ truckId }: IProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<IChangeTarget<ITruck> | undefined>(
    undefined,
  );

  const renderField = (field: keyof ITruck, isAfter = false) => {
    const beforeValue = data?.before?.[field];
    const afterValue = data?.after?.[field];
    const label = t(`general.${field}`);
    const changed = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);

    if (
      field === 'services' &&
      Array.isArray(beforeValue) &&
      Array.isArray(afterValue)
    ) {
      const list = isAfter ? afterValue : beforeValue;

      return (
        <div key={field + (isAfter ? '-after' : '-before')} className="mb-2">
          <p className="fw-bold mb-1">{label}:</p>
          {list.map((service: ITruckService, index: number) => {
            const matching = (isAfter ? beforeValue : afterValue).find(
              (s: ITruckService) => s.type === service.type,
            );
            const isChanged =
              !matching || JSON.stringify(matching) !== JSON.stringify(service);
            return (
              <div
                key={service.type + index}
                className={`ms-3 ${isChanged && isAfter ? 'text-warning fw-semibold' : ''}`}
              >
                - {t(`service_type.${service.type}`)} (
                {formatNumber(service.mileage as number)} km)
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div
        key={field + (isAfter ? '-after' : '-before')}
        className="d-flex align-items-center mb-1"
      >
        <p className="fw-bold mb-0">{label}</p>:
        <span
          className={`ms-1 ${changed && isAfter ? 'text-warning fw-semibold' : ''}`}
        >
          {(isAfter ? afterValue : beforeValue) as string}
        </span>
      </div>
    );
  };

  useEffect(() => {
    if (truckId) {
      AuthService.getChangeTarget<ITruck>({
        targetId: String(truckId),
        target: EChangeTarget.TRUCK,
      })
        .then(setData)
        .catch(handleError);
    }
  }, [truckId]);
  return (
    <>
      <Row style={{ maxHeight: 300 }} className="overflow-auto">
        {data && (
          <>
            <Col md={6} className={'border-end'}>
              <h5 className={'mb-3 fw-bold'}>{t('general.oldData')}:</h5>

              {renderField('licensePlate')}
              {renderField('mileage')}
              {renderField('nextServiceKm')}
              {renderField('technicalInspection')}
              {renderField('tachograph')}
              {renderField('alarmSystem')}
              {renderField('services')}
            </Col>
            <Col md={6}>
              <h5 className={'mb-3 fw-bold'}>{t('general.newData')}:</h5>
              {renderField('licensePlate', true)}
              {renderField('mileage', true)}
              {renderField('nextServiceKm', true)}
              {renderField('technicalInspection', true)}
              {renderField('tachograph', true)}
              {renderField('alarmSystem', true)}
              {renderField('services', true)}
            </Col>
          </>
        )}
      </Row>
      <Row>
        <Col md={12} className={'border-top pt-3'}>
          <div className="d-flex align-items-center">
            <h6 className={'fw-bold'}>{t('general.requestedBy')}</h6>
            <span className={'mx-1'}>- {data?.changedBy?.displayName} -</span>
            {formatDate(data?.updatedAt as string, 'DD.MM.YYYY HH:mm:ss')}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default TruckPreview;
